import * as React from 'react';
import { useRef, useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useListHunts, getGetHuntDetailQueryOptions } from '@cdf/api-client-react';
import {
  useCvTargets,
  useCreateCvTarget,
  useUpdateCvTarget,
  useDeleteCvTarget,
  useTrainCvTarget,
  useTestCvSample,
  CELEBRATION_EFFECT_OPTIONS,
  type CelebrationEffect,
  type CvTarget,
  type CvTestResult
} from '@/lib/cv-api';
import { PageHeader } from '@/components/layout/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  UploadIcon,
  ScanIcon,
  Trash2Icon,
  RefreshCwIcon,
  ImageIcon,
  TargetIcon,
  CheckCircle2Icon,
  XCircleIcon,
  CrosshairIcon,
  PlusIcon
} from 'lucide-react';

// ── Drag-and-drop file zone ─────────────────────────────────────────────────

interface DropzoneProps {
  onFile: (file: File) => void;
  label: string;
  accept?: string;
  disabled?: boolean;
}

function Dropzone({ onFile, label, accept = 'image/*', disabled }: DropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);

  const handleFile = useCallback(
    (file: File) => {
      setPreview(URL.createObjectURL(file));
      onFile(file);
    },
    [onFile]
  );

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => inputRef.current?.click()}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
      }}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setIsDragging(true);
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragging(false);
        if (disabled) return;
        const file = e.dataTransfer.files[0];
        if (file && file.type.startsWith('image/')) handleFile(file);
      }}
      className={`flex min-h-[160px] cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6 transition-colors ${
        isDragging
          ? 'border-primary bg-primary/5'
          : 'border-muted-foreground/30 hover:border-muted-foreground/50'
      } ${disabled ? 'pointer-events-none opacity-50' : ''}`}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFile(file);
        }}
      />
      {preview ? (
        <img src={preview} alt="Preview" className="max-h-32 rounded-md object-contain" />
      ) : (
        <>
          <UploadIcon className="h-8 w-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className="text-xs text-muted-foreground/70">PNG, JPEG, GIF · max 20 MB</p>
        </>
      )}
    </div>
  );
}

// ── Color histogram bar chart ───────────────────────────────────────────────

function ColorHistogram({ buckets }: { buckets: number[] }) {
  const max = Math.max(...buckets, 0.001);
  const colors = [
    '#1e3a5f', '#2d5a8b', '#3b7ab8', '#4a9ae6', '#1e5f3a', '#2d8050',
    '#3ba066', '#4ac084', '#5f3a1e', '#80502d', '#a0663b', '#c0844a',
    '#5f1e3a', '#802d50', '#a03b66', '#c04a84'
  ];
  return (
    <div className="flex h-16 items-end gap-0.5">
      {buckets.map((v, i) => (
        <div
          key={i}
          className="flex-1 rounded-t-sm transition-all"
          style={{
            height: `${(v / max) * 100}%`,
            backgroundColor: colors[i % colors.length],
            minHeight: '2px'
          }}
        />
      ))}
    </div>
  );
}

// ── Target card ─────────────────────────────────────────────────────────────

function celebrationEffectLabel(effect: CelebrationEffect): string {
  return CELEBRATION_EFFECT_OPTIONS.find((option) => option.value === effect)?.label ?? 'None';
}

interface TargetCardProps {
  target: CvTarget;
  onEdit: () => void;
  onTrain: () => void;
  onDelete: () => void;
  training: boolean;
}

function TargetCard({ target, onEdit, onTrain, onDelete, training }: TargetCardProps) {
  return (
    <Card className={target.isActive ? '' : 'opacity-60'}>
      <CardHeader className="flex flex-row items-start justify-between gap-2 pb-3">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10">
            <TargetIcon className="h-5 w-5 text-primary" />
          </div>
          <div>
            <CardTitle className="text-base">{target.name}</CardTitle>
            {target.description && (
              <p className="text-sm text-muted-foreground">{target.description}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={onEdit} title="Edit target">
            <CrosshairIcon className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={onTrain}
            disabled={training}
            title="Re-extract features"
          >
            <RefreshCwIcon className={`h-4 w-4 ${training ? 'animate-spin' : ''}`} />
          </Button>
          <Button variant="ghost" size="icon" onClick={onDelete} title="Delete target">
            <Trash2Icon className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={target.isActive ? 'default' : 'secondary'}>
            {target.isActive ? 'Active' : 'Inactive'}
          </Badge>
          <Badge variant="outline">
            Threshold: {Math.round(target.confidenceThreshold * 100)}%
          </Badge>
          <Badge variant={target.celebrationEffect === 'none' ? 'secondary' : 'outline'}>
            Celebration: {celebrationEffectLabel(target.celebrationEffect)}
          </Badge>
          {target.huntStationId && (
            <Badge variant="outline" className="text-primary">
              <ImageIcon className="mr-1 h-3 w-3" />
              Station bound
            </Badge>
          )}
        </div>

        {target.descriptorSummary && (
          <div className="rounded-md bg-muted/50 p-3 space-y-2">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-muted-foreground">Dimensions: </span>
                <span className="font-medium">
                  {target.descriptorSummary.width || '?'} ×{' '}
                  {target.descriptorSummary.height || '?'}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Size: </span>
                <span className="font-medium">
                  {(target.descriptorSummary.sizeBytes / 1024).toFixed(1)} KB
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Brightness: </span>
                <span className="font-medium">
                  {(target.descriptorSummary.brightness * 100).toFixed(0)}%
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Edge: </span>
                <span className="font-medium">
                  {(target.descriptorSummary.edgeScore * 100).toFixed(1)}%
                </span>
              </div>
            </div>
            <div>
              <p className="mb-1 text-xs text-muted-foreground">Color distribution</p>
              <ColorHistogram buckets={target.descriptorSummary.colorBuckets} />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Create dialog ───────────────────────────────────────────────────────────

function CreateTargetDialog() {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [threshold, setThreshold] = useState(0.7);
  const [celebrationEffect, setCelebrationEffect] = useState<CelebrationEffect>('fireworks_streamers');
  const createMutation = useCreateCvTarget();

  const reset = () => {
    setFile(null);
    setName('');
    setDescription('');
    setThreshold(0.7);
    setCelebrationEffect('fireworks_streamers');
  };

  const handleSubmit = async () => {
    if (!file) return;
    try {
      await createMutation.mutateAsync({
        file,
        name: name || file.name.replace(/\.[^.]+$/, ''),
        description: description || undefined,
        confidenceThreshold: threshold,
        celebrationEffect
      });
      reset();
      setOpen(false);
    } catch {
      // error shown via mutation state
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button>
          <PlusIcon className="mr-2 h-4 w-4" />
          New CV Target
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Create CV Target</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Dropzone onFile={setFile} label="Drop a reference image (logo, signage, booth object)" />
          {file && (
            <p className="text-xs text-muted-foreground">Selected: {file.name} ({(file.size / 1024).toFixed(1)} KB)</p>
          )}
          <div>
            <Label htmlFor="target-name">Name</Label>
            <Input
              id="target-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Circle T Booth Logo"
            />
          </div>
          <div>
            <Label htmlFor="target-desc">Description (optional)</Label>
            <Textarea
              id="target-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Stand 1 — Circle T Software Foundry signage"
              rows={2}
            />
          </div>
          <div>
            <Label htmlFor="threshold">Confidence threshold: {Math.round(threshold * 100)}%</Label>
            <input
              type="range"
              id="threshold"
              min={0}
              max={1}
              step={0.05}
              value={threshold}
              onChange={(e) => setThreshold(parseFloat(e.target.value))}
              className="w-full accent-primary"
            />
            <p className="text-xs text-muted-foreground">Minimum similarity required for a match in the AR scanner.</p>
          </div>
          <div>
            <Label htmlFor="target-celebration">Celebration effect</Label>
            <Select value={celebrationEffect} onValueChange={(value) => setCelebrationEffect(value as CelebrationEffect)}>
              <SelectTrigger id="target-celebration">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CELEBRATION_EFFECT_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-muted-foreground">Shown when this target is detected in the AR scanner.</p>
          </div>
        </div>
        {createMutation.isError && (
          <p className="text-sm text-destructive">
            {(createMutation.error as Error)?.message ?? 'Failed to create target.'}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={!file || createMutation.isPending}>
            {createMutation.isPending ? 'Creating…' : 'Create Target'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Edit dialog ─────────────────────────────────────────────────────────────

interface EditDialogProps {
  target: CvTarget | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

function EditTargetDialog({ target, open, onOpenChange }: EditDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [threshold, setThreshold] = useState(0.7);
  const [celebrationEffect, setCelebrationEffect] = useState<CelebrationEffect>('fireworks_streamers');
  const [isActive, setIsActive] = useState(true);
  const [huntId, setHuntId] = useState<string>('');
  const [stationId, setStationId] = useState<string>('');
  const updateMutation = useUpdateCvTarget();

  // Sync state when target changes
  React.useEffect(() => {
    if (target) {
      setName(target.name);
      setDescription(target.description ?? '');
      setThreshold(target.confidenceThreshold);
      setCelebrationEffect(target.celebrationEffect);
      setIsActive(target.isActive);
      setHuntId('');
      setStationId(target.huntStationId ?? '');
    }
  }, [target]);

  const { data: huntsData } = useListHunts();
  const hunts = huntsData?.hunts ?? [];

  const { data: huntDetail } = useQuery({
    ...getGetHuntDetailQueryOptions(huntId || ''),
    enabled: !!huntId
  });
  const stations = huntDetail?.stations ?? [];

  const handleSubmit = async () => {
    if (!target) return;
    try {
      await updateMutation.mutateAsync({
        id: target.id,
        name,
        description: description || undefined,
        confidenceThreshold: threshold,
        celebrationEffect,
        huntStationId: stationId || null,
        isActive
      });
      onOpenChange(false);
    } catch {
      // error shown via mutation state
    }
  };

  if (!target) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit CV Target — {target.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="edit-name">Name</Label>
            <Input id="edit-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="edit-desc">Description</Label>
            <Textarea
              id="edit-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>
          <div>
            <Label htmlFor="edit-threshold">Confidence threshold: {Math.round(threshold * 100)}%</Label>
            <input
              type="range"
              id="edit-threshold"
              min={0}
              max={1}
              step={0.05}
              value={threshold}
              onChange={(e) => setThreshold(parseFloat(e.target.value))}
              className="w-full accent-primary"
            />
          </div>
          <div>
            <Label htmlFor="edit-celebration">Celebration effect</Label>
            <Select value={celebrationEffect} onValueChange={(value) => setCelebrationEffect(value as CelebrationEffect)}>
              <SelectTrigger id="edit-celebration">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CELEBRATION_EFFECT_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-muted-foreground">Shown when this target is detected in the AR scanner.</p>
          </div>
          <div className="flex items-center justify-between rounded-md border p-3">
            <div>
              <Label htmlFor="edit-active">Active</Label>
              <p className="text-xs text-muted-foreground">Inactive targets are ignored by the AR scanner.</p>
            </div>
            <Switch id="edit-active" checked={isActive} onCheckedChange={setIsActive} />
          </div>

          <div className="space-y-3 rounded-md border p-3">
            <h4 className="text-sm font-semibold">Hunt Station Binding</h4>
            <div>
              <Label htmlFor="hunt-select">Hunt</Label>
              <Select value={huntId} onValueChange={(v) => { setHuntId(v); setStationId(''); }}>
                <SelectTrigger id="hunt-select">
                  <SelectValue placeholder="Select a hunt…" />
                </SelectTrigger>
                <SelectContent>
                  {hunts.map((h) => (
                    <SelectItem key={h.id} value={h.id}>
                      {h.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {huntId && stations.length > 0 && (
              <div>
                <Label htmlFor="station-select">Station</Label>
                <Select value={stationId} onValueChange={setStationId}>
                  <SelectTrigger id="station-select">
                    <SelectValue placeholder="Select a station…" />
                  </SelectTrigger>
                  <SelectContent>
                    {stations.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name} ({s.kind})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {huntId && stations.length === 0 && (
              <p className="text-xs text-muted-foreground">No stations found for this hunt.</p>
            )}
            {!huntId && target.huntStationId && (
              <p className="text-xs text-muted-foreground">
                Currently bound to a station. Select a hunt above to change.
              </p>
            )}
          </div>
        </div>
        {updateMutation.isError && (
          <p className="text-sm text-destructive">
            {(updateMutation.error as Error)?.message ?? 'Failed to update target.'}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={updateMutation.isPending}>
            {updateMutation.isPending ? 'Saving…' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Confidence tester panel ─────────────────────────────────────────────────

function ConfidenceTester() {
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<CvTestResult | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const testMutation = useTestCvSample();
  const { data: targetsData } = useCvTargets();

  const handleFile = useCallback((f: File) => {
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setResult(null);
  }, []);

  const handleTest = async () => {
    if (!file) return;
    try {
      const res = await testMutation.mutateAsync({ file });
      setResult(res);
    } catch {
      // error via mutation state
    }
  };

  return (
    <div className="space-y-4">
      <Dropzone onFile={handleFile} label="Drop a sample photo to test against trained targets" />
      {preview && (
        <div className="flex items-center gap-3">
          <img src={preview} alt="Sample" className="max-h-24 rounded-md object-contain" />
          <div className="text-sm text-muted-foreground">
            {file?.name} ({file ? (file.size / 1024).toFixed(1) : 0} KB)
          </div>
        </div>
      )}

      <Button onClick={handleTest} disabled={!file || testMutation.isPending}>
        <ScanIcon className="mr-2 h-4 w-4" />
        {testMutation.isPending ? 'Scanning…' : 'Run Detection Test'}
      </Button>

      {testMutation.isError && (
        <p className="text-sm text-destructive">
          {(testMutation.error as Error)?.message ?? 'Test failed.'}
        </p>
      )}

      {result && (
        <div className="space-y-4">
          {/* Sample descriptor summary */}
          <div className="rounded-md bg-muted/50 p-3">
            <h4 className="mb-2 text-sm font-semibold">Sample Feature Summary</h4>
            <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
              <div>
                <span className="text-muted-foreground">Dimensions: </span>
                <span className="font-medium">{result.sample.width || '?'} × {result.sample.height || '?'}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Size: </span>
                <span className="font-medium">{(result.sample.sizeBytes / 1024).toFixed(1)} KB</span>
              </div>
              <div>
                <span className="text-muted-foreground">Brightness: </span>
                <span className="font-medium">{(result.sample.brightness * 100).toFixed(0)}%</span>
              </div>
              <div>
                <span className="text-muted-foreground">Edge: </span>
                <span className="font-medium">{(result.sample.edgeScore * 100).toFixed(1)}%</span>
              </div>
            </div>
          </div>

          {/* Match results */}
          <div className="space-y-2">
            <h4 className="text-sm font-semibold">Match Results ({result.results.length} targets)</h4>
            {result.results.length === 0 && (
              <p className="text-sm text-muted-foreground">No active targets registered.</p>
            )}
            {result.results.map((r) => (
              <div
                key={r.targetId}
                className="flex items-center justify-between rounded-md border p-3"
              >
                <div className="flex items-center gap-3">
                  {r.matched ? (
                    <CheckCircle2Icon className="h-5 w-5 text-green-500" />
                  ) : (
                    <XCircleIcon className="h-5 w-5 text-muted-foreground" />
                  )}
                  <div>
                    <p className="text-sm font-medium">{r.targetName}</p>
                    <p className="text-xs text-muted-foreground">
                      Threshold: {Math.round(r.threshold * 100)}%
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="h-2 w-32 overflow-hidden rounded-full bg-muted">
                    <div
                      className={`h-full rounded-full ${r.matched ? 'bg-green-500' : 'bg-primary/40'}`}
                      style={{ width: `${Math.min(100, r.score * 100)}%` }}
                    />
                  </div>
                  <span className={`text-sm font-medium ${r.matched ? 'text-green-600' : ''}`}>
                    {Math.round(r.score * 100)}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {targetsData && targetsData.total === 0 && (
        <p className="text-sm text-muted-foreground">
          No CV targets registered yet. Create a target first, then test sample photos against it.
        </p>
      )}
    </div>
  );
}

// ── Main page ───────────────────────────────────────────────────────────────

export function CvTrainingPage() {
  const { data, isLoading, isError, error } = useCvTargets();
  const [editing, setEditing] = useState<CvTarget | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const trainMutation = useTrainCvTarget();
  const deleteMutation = useDeleteCvTarget();

  const handleEdit = (target: CvTarget) => {
    setEditing(target);
    setEditOpen(true);
  };

  const handleDelete = async (target: CvTarget) => {
    if (!confirm(`Delete CV target "${target.name}"? This cannot be undone.`)) return;
    try {
      await deleteMutation.mutateAsync(target.id);
    } catch {
      // error handled via mutation state
    }
  };

  const handleTrain = async (target: CvTarget) => {
    try {
      await trainMutation.mutateAsync(target.id);
    } catch {
      // non-fatal
    }
  };

  const targets = data?.targets ?? [];

  return (
    <div>
      <PageHeader
        title="CV Training Studio"
        description="Register, inspect, and train computer vision targets for the AR scanner."
      />

      <div className="p-4 md:p-6">
        <Tabs defaultValue="targets">
          <TabsList>
            <TabsTrigger value="targets">
              <TargetIcon className="mr-2 h-4 w-4" />
              Targets ({targets.length})
            </TabsTrigger>
            <TabsTrigger value="tester">
              <ScanIcon className="mr-2 h-4 w-4" />
              Confidence Tester
            </TabsTrigger>
          </TabsList>

          <TabsContent value="targets" className="mt-4 space-y-4">
            <div className="flex justify-end">
              <CreateTargetDialog />
            </div>

            {isLoading && (
              <div className="py-12 text-center text-muted-foreground">
                <RefreshCwIcon className="mx-auto mb-2 h-6 w-6 animate-spin" />
                Loading targets…
              </div>
            )}

            {isError && (
              <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
                {(error as Error)?.message ?? 'Failed to load CV targets.'}
                <p className="mt-1 text-xs">
                  Ensure App Storage is enabled in Settings to use CV targets.
                </p>
              </div>
            )}

            {!isLoading && !isError && targets.length === 0 && (
              <div className="rounded-md border border-dashed py-12 text-center">
                <TargetIcon className="mx-auto mb-3 h-10 w-10 text-muted-foreground/50" />
                <p className="text-sm text-muted-foreground">
                  No CV targets yet. Click <strong>New CV Target</strong> to upload a reference image.
                </p>
              </div>
            )}

            {!isLoading && targets.length > 0 && (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {targets.map((t) => (
                  <TargetCard
                    key={t.id}
                    target={t}
                    onEdit={() => handleEdit(t)}
                    onTrain={() => handleTrain(t)}
                    onDelete={() => handleDelete(t)}
                    training={trainMutation.isPending}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="tester" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ScanIcon className="h-5 w-5" />
                  Detection Confidence Tester
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ConfidenceTester />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <EditTargetDialog target={editing} open={editOpen} onOpenChange={setEditOpen} />
    </div>
  );
}
